import React, { FormEvent, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Text,
  useToast,
  FormErrorMessage,
  useColorModeValue,
} from '@chakra-ui/react';

import CenteredAuth from 'layouts/auth/variants/Centered';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import authRepository from 'api/authRepository';
import { resetPasswordSchema, flattenZodErrors } from 'validations/Validation'

function NewPassword() {
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [code, setCode] = useState(
    searchParams.get('token') ||
      searchParams.get('code') ||
      searchParams.get('resetToken') ||
      ''
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const textColor = useColorModeValue('#2D2A5E', 'white');
  const textColorSecondary = 'gray.400';
  const brandStars = useColorModeValue('#7062B9', '#A89CE8');
  const inputBg = useColorModeValue('#EEEBFA', '#1B254B');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const resetToken = code.trim();
    if (!resetToken) {
      setErrors({ token: 'Reset token is required.' });
      return;
    }

    try {
      setErrors({});

      const validation = resetPasswordSchema.safeParse({
        newPassword: password,
        confirmPassword: confirmPassword,
      });

      if (!validation.success) {
        const newErrors = flattenZodErrors(validation.error);
        setErrors(newErrors);
        toast({
          title: 'Please check your password',
          description: validation.error.issues.map((issue) => issue.message).join(' '),
          status: 'warning',
          duration: 7000,
          isClosable: true,
          position: 'top',
        });
        return;
      }

      setIsSubmitting(true);
      await authRepository.resetPassword(resetToken, password);

      toast({
        title: "Success",
        description: "Your password has been reset successfully.",
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
      navigate("/auth/sign-in");

    } catch (error: unknown) {
      console.error("Error submitting form:", error);
      const apiError = error as {
        response?: {
          data?: {
            message?: string;
            errors?: Record<string, string[] | string>;
          } | string;
        };
        message?: string;
      };
      const validationDetails =
        apiError.response?.data &&
        typeof apiError.response.data === 'object' &&
        apiError.response.data.errors
          ? Object.values(apiError.response.data.errors)
              .flatMap((messages) =>
                Array.isArray(messages) ? messages : [messages]
              )
              .join(' ')
          : '';
      const message =
        validationDetails ||
        (typeof apiError.response?.data === 'object'
          ? apiError.response.data.message
          : apiError.response?.data) ||
        apiError.message ||
        'Failed to reset password.';
      toast({
        title: "Error",
        description: message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <CenteredAuth
      cardTop={{ base: '140px', md: '24vh' }}
      cardBottom={{ base: '50px', lg: 'auto' }}
    >
      <Flex
        w="100%"
        maxW="max-content"
        me="auto"
        h="100%"
        alignItems="start"
        justifyContent="center"
        px={{ base: '25px', md: '0px' }}
        flexDirection="column"
      >
        <Box me="auto" mb="34px">
          <Heading
            color={textColor}
            fontSize={{ base: '3xl', md: '36px' }}
            mb="16px"
          >
            Enter Your New Password
          </Heading>
          <Text
            color={textColorSecondary}
            fontSize="md"
            w={{ base: '100%', lg: '456px' }}
            maxW="100%"
          >
            Please enter your token and set a strong new password.
          </Text>
        </Box>
        <Flex
          zIndex="2"
          direction="column"
          w={{ base: '100%', lg: '456px' }}
          maxW="100%"
          background="transparent"
          borderRadius="15px"
          mx={{ base: 'auto', lg: 'unset' }}
          me="auto"
          mb={{ base: '20px', md: 'auto' }}
          align="start"
        >
          <form onSubmit={handleSubmit} style={{ width: '100%' }}>
          <FormControl isInvalid={!!errors['token']}>
            <FormLabel
              display="flex"
              ms="4px"
              fontSize="sm"
              fontWeight="500"
              color={textColor}
              mb="8px"
            >
              Enter Your Token<Text color={brandStars}>*</Text>
            </FormLabel>
            <Input
              isRequired
              variant="auth"
              bg={inputBg}
              color={textColor}
              borderColor="transparent"
              borderRadius="14px"
              fontSize="sm"
              type="text"
              placeholder="Enter Your OTP"
              mb="24px"
              size="lg"
              h="52px"
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                if (errors.token) {
                  setErrors((current) => ({ ...current, token: '' }));
                }
              }}
              autoComplete="one-time-code"
              _focus={{
                borderColor: '#7062B9',
                boxShadow: '0 0 0 1px #7062B9',
              }}
              isReadOnly={!!(
                searchParams.get('token') ||
                searchParams.get('code') ||
                searchParams.get('resetToken')
              )}
            />
            <FormErrorMessage>{errors['token']}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors['newPassword']}>
            <FormLabel
              display="flex"
              ms="4px"
              fontSize="sm"
              fontWeight="500"
              color={textColor}
              mb="8px"
            >
              New Password<Text color={brandStars}>*</Text>
            </FormLabel>
            <Input
              isRequired
              variant="auth"
              bg={inputBg}
              color={textColor}
              borderColor="transparent"
              borderRadius="14px"
              fontSize="sm"
              type="password"
              placeholder="Enter Your New Password"
              mb="24px"
              size="lg"
              h="52px"
              value={password}
              _focus={{
                borderColor: '#7062B9',
                boxShadow: '0 0 0 1px #7062B9',
              }}
              onChange={(e) => {
                setPassword(e.target.value);
                if (errors.newPassword) {
                  setErrors((current) => ({ ...current, newPassword: '' }));
                }
              }}
            />
            <FormErrorMessage>{errors['newPassword']}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors['confirmPassword']}>
            <FormLabel
              display="flex"
              ms="4px"
              fontSize="sm"
              fontWeight="500"
              color={textColor}
              mb="8px"
            >
              Confirm Password<Text color={brandStars}>*</Text>
            </FormLabel>
            <Input
              isRequired
              variant="auth"
              bg={inputBg}
              color={textColor}
              borderColor="transparent"
              borderRadius="14px"
              fontSize="sm"
              type="password"
              placeholder="Confirm New Password"
              mb="24px"
              size="lg"
              h="52px"
              value={confirmPassword}
              _focus={{
                borderColor: '#7062B9',
                boxShadow: '0 0 0 1px #7062B9',
              }}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                if (errors.confirmPassword) {
                  setErrors((current) => ({ ...current, confirmPassword: '' }));
                }
              }}
            />
            <FormErrorMessage>{errors['confirmPassword']}</FormErrorMessage>
          </FormControl>

          <Button
            type="submit"
            fontSize="sm"
            bg="#7062B9"
            color="white"
            borderRadius="14px"
            fontWeight="500"
            w="100%"
            h="50px"
            mb="24px"
            _hover={{ bg: '#5B4FA3' }}
            isLoading={isSubmitting}
            isDisabled={isSubmitting}
          >
            Save New Password
          </Button>
          </form>
        </Flex>
        <RouterLink to="/auth/forgot-password">
          <Text color={brandStars} fontSize="sm" fontWeight="600">
            Request a new reset token
          </Text>
        </RouterLink>
      </Flex>
    </CenteredAuth>
  );
}

export default NewPassword;
